import React, { FC, useState } from 'react';
import styled from 'styled-components';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers';
import { useHistory } from 'react-router-dom';
import {
  TextInput,
  InputType,
  Button,
  ButtonSize,
  ButtonColor,
  Captcha as CaptchaComponent,
} from '@code/innovance-react-ui-kit';
import { Trans, useTranslation } from 'react-i18next';
import { Md5 } from 'ts-md5';

import { Login, Captcha, updateHeaderConfig } from 'src/front/api/apiInstance';
import { LoginSchema } from 'src/front/common/utils/validationSchemas';
import { formatAlphaNumeric, formatBlank } from 'src/front/common/utils/formUtils';
import { formatToISODate } from 'src/front/common/utils/dateTimeUtils';

import { useLanguageState, useLanguageDispatch, Languages } from 'src/front/contexts/I18nContext';
import {
  useUserInfoDispatch,
  useMenuDispatch,
  useProfileMenuDispatch,
} from 'src/front/contexts/SessionContext';

import { DASHBOARD, CHANGE_PASSWORD } from 'src/front/routes/paths';
import { BREAK_POINTS } from 'src/front/constant';

import logoEn from 'src/front/assets/img/logo_cebfiba_en_white.png';
import logoRu from 'src/front/assets/img/logo_cebfiba_rus_white.png';
import backgroundImage from 'src/front/assets/img/bg.jpg';

const { NODE_ENV, REACT_APP_ENVIRONMENT } = process.env;
const version = process.env.REACT_APP_VERSION;
const isLanguageChangeDisplayed =
  NODE_ENV === 'development' || REACT_APP_ENVIRONMENT === 'DEV' || REACT_APP_ENVIRONMENT === 'UAT';

interface FormData {
  username: string;
  password: string;
  captcha: string;
}

// TODO(ramazan.guclu): font-family will be added related components from ui kit later
const Container = styled.div`
  position: fixed;

  width: 100vw;
  height: 100vh;

  background: url(${backgroundImage});
  background-repeat: no-repeat;
  background-size: cover;
  overflow-y: auto;

  & > * {
    display: block;
    float: left;
  }
`;

const WidgetContainer = styled.form`
  position: relative;

  box-sizing: border-box;
  width: 432px;
  padding: 53px 45px 31px;
  border-radius: 6px;

  /* TODO (ozgunbal): needs to be added to theme of the application */
  background-color: #121c24; /* ${({ theme }) => theme.palette.dark}; */

  @media screen and (min-width: ${BREAK_POINTS.MIN.TABLET}) {
    margin-left: 154px;
    margin-top: 85px;
    margin-bottom: 123px;
  }

  @media screen and (min-width: ${BREAK_POINTS.MIN.PHONE}) and (max-width: ${
  BREAK_POINTS.MAX.TABLET
}) {
    margin-left: 30px;
    margin-top: 85px;
    margin-bottom: 133px;
  }

  @media screen and (min-width: ${BREAK_POINTS.MIN.TABLET}) and (max-width: ${
  BREAK_POINTS.MAX.LARGE_1X
}) {
    margin-left: 122px;
    margin-top: 85px;
    margin-bottom: 123px;
  }

  @media screen and (max-width: ${BREAK_POINTS.MAX.PHONE}) {
    width: 100%;
    border-radius: 0;
    margin: 0;
  }

  @media screen and (max-width: ${BREAK_POINTS.MAX.PHONE}) and (orientation: portrait) {
    height: 100%;
  }
`;

const WidgetHeader = styled.div``;

const HeaderText = styled.h1`
  height: 24px;
  margin: 0;
  margin-bottom: 10px;

  color: ${({ theme }) => theme.palette.white};

  font-size: 18px;
  line-height: 1.3;
  font-weight: 400;
`;

const Logo = styled.div`
  width: auto;

  > img {
    width: 100%;
  }
`;

const LoginButton = styled(Button)`
  height: 50px;
`;

const Copyright = styled.p`
  margin-top: 35px;

  color: ${({ theme }) => theme.palette.greenyGrey};

  font-size: 12px;
  text-align: center;
  font-weight: 400;

  @media screen and (max-width: ${BREAK_POINTS.MAX.PHONE}) and (orientation: portrait) {
    position: absolute;
    right: 0;
    bottom: 10px;
    left: 0;
  }
`;

const VersionContainer = styled(Copyright)`
  margin: 0;
`;

const Error = styled.p`
  margin: 0;
  margin-bottom: 30px;

  color: ${({ theme }) => theme.palette.lipstick};
`;

const TextInputUserName = styled(TextInput)`
  margin-top: 50px;
  margin-bottom: 12px;
`;

const TextInputUserPassword = styled(TextInput)`
  margin-bottom: 30px;
`;

const CaptchaWithMargin = styled(CaptchaComponent)`
  margin-bottom: 31px;
`;

const Information = styled.div`
  width: 35%;
  margin-left: 10%;
  margin-top: 110px;

  color: ${({ theme }) => theme.palette.white};

  @media screen and (max-width: ${BREAK_POINTS.MAX.TABLET}) {
    margin-left: 5%;
    margin-top: 110px;
  }

  @media screen and (max-width: ${BREAK_POINTS.MAX.PHONE}) {
    width: 85%;
    padding: 20px;
    margin: 0;
  }
`;

const InformationTitle = styled.p`
  font-size: 19.8px;
  font-weight: bold;
`;

const InformationParagraph = styled.p`
  font-size: 16.2px;
`;

const LanguageButton = styled.div`
  position: absolute;
  top: 16px;
  right: 16px;

  padding: 0 5px;
  border: solid 1px rgba(195, 203, 215, 0.3);
  border-radius: 3px;

  color: ${({ theme }) => theme.palette.white};

  cursor: pointer;
`;

/* temporary function will be deleted after i18n work */
const getLoginErrorMessage = (message: string): string => {
  interface MessageObject {
    CAPTCHA_VALIDATION_REQUIRED: string;
    INVALID_CAPTCHA: string;
    INVALID_CREDENTIALS: string;
    BLOCKED_USER: string;
    [key: string]: string;
  }

  const messages: MessageObject = {
    CAPTCHA_VALIDATION_REQUIRED: 'general_captchaValidationRequired_errorMessage',
    INVALID_CAPTCHA: 'general_incorrectCaptcha_errorMessage',
    INVALID_CREDENTIALS: 'login_login_invalidCredential_errorMessage',
    BLOCKED_USER: 'login_blockedUser_errorMessage',
  };

  return messages[message] || message;
};

export const LoginWidget: FC<{}> = () => {
  const { t } = useTranslation();
  const setLanguage = useLanguageDispatch();
  const language = useLanguageState();

  const history = useHistory();

  const [showCaptcha, setShowCaptcha] = useState(false);
  const [captchaImage, setCaptchaImage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const {
    register,
    control,
    handleSubmit,
    getValues,
    watch,
    formState,
    errors,
    setValue,
  } = useForm<FormData>({
    defaultValues: { username: '', password: '', captcha: '' },
    resolver: yupResolver(LoginSchema),
  });
  const setUserInfo = useUserInfoDispatch();
  const setMenu = useMenuDispatch();
  const setProfileMenu = useProfileMenuDispatch();

  const changeLanguage = (): void =>
    setLanguage(language === Languages.Russian ? Languages.English : Languages.Russian);

  const onSubmit = async (values: FormData): Promise<void> => {
    let validate = true;
    if (showCaptcha) {
      validate = await handleValidate();
    }
    if (validate === true) {
      try {
        setLoading(true);
        const { data, headers } = await Login.login({
          username: values.username,
          password: String(Md5.hashStr(values.password)),
        });

        setLoading(false);

        updateHeaderConfig(data.loginSessionID, headers.authorization);

        setUserInfo({
          isLoggedIn: true,
          userCode: data.userCode,
          userProfileImageName: data.userProfileImageName,
          processDate: formatToISODate(data.processDate),
          isAuthenticationRestricted: !!data.isAuthenticationRestricted,
        });
        // TODO menuList is really optional ask for MW developers
        setMenu(data.menuList || []);
        setProfileMenu(data.profileMenuList || []);

        const newPath = data.isAuthenticationRestricted ? CHANGE_PASSWORD : DASHBOARD;

        history.push(newPath);
      } catch (data) {
        if (data.response.data.showCaptcha) {
          setShowCaptcha(true);
          handleRefreshCaptcha();
        } else {
          setShowCaptcha(false);
        }

        setErrorMessage(getLoginErrorMessage(data.response.data.error.errorMessage));
      }
    }
  };

  const handleRefreshCaptcha = async (): Promise<void> => {
    setCaptchaImage('');
    try {
      setLoading(true);
      const { data } = await Captcha.getCaptcha();
      setLoading(false);
      setCaptchaImage(data.captchaImage);
    } catch (data) {
      setErrorMessage(getLoginErrorMessage(data.response.data.error.errorMessage));
    }
  };

  const handleValidate = async (): Promise<boolean> => {
    try {
      const captcha = getValues('captcha');
      await Captcha.validateCaptcha({ captcha });
      return true;
    } catch (data) {
      handleRefreshCaptcha();
      setErrorMessage(getLoginErrorMessage(data.response.data.error.errorMessage));
      return false;
    }
  };

  const handleChangeCaptcha = (text: string): void => setValue('captcha', formatBlank(text));

  return (
    <Container>
      <WidgetContainer onSubmit={handleSubmit(onSubmit)}>
        {isLanguageChangeDisplayed ? (
          <LanguageButton onClick={changeLanguage}>
            {language === Languages.Russian ? 'EN' : 'RU'}
          </LanguageButton>
        ) : null}
        <WidgetHeader>
          <HeaderText>
            <Trans i18nKey="login_login_welcome_text" />
          </HeaderText>
          <Logo>
            <img alt="logo" src={language === Languages.Russian ? logoRu : logoEn} />
          </Logo>
        </WidgetHeader>

        <Controller
          control={control}
          name="username"
          render={({ onChange, value }) => (
            <TextInputUserName
              errorMessage={t(errors?.username?.message as string)}
              formatter={formatAlphaNumeric}
              label={t('login_login_username_label')}
              maxLength={10}
              name="username"
              onChange={onChange}
              value={value}
              isDark
            />
          )}
        />
        <Controller
          control={control}
          name="password"
          render={({ onChange, value }) => (
            <TextInputUserPassword
              errorMessage={t(errors?.password?.message as string)}
              formatter={formatBlank}
              label={t('login_login_password_label')}
              maxLength={15}
              name="password"
              onChange={onChange}
              type={InputType.Password}
              value={value}
              isDark
            />
          )}
        />

        {errorMessage ? <Error>{t(errorMessage)}</Error> : null}
        {showCaptcha ? (
          // Add controller to captcha after formatter adjustment
          <CaptchaWithMargin
            disabled={loading}
            image={`data:image/png;base64,${captchaImage}`}
            inputName="captcha"
            inputRegister={register}
            label={t('general_components_captchaSecurityCode_label')}
            onChange={handleChangeCaptcha}
            onRefresh={handleRefreshCaptcha}
            value={watch('captcha')}
          />
        ) : null}
        <LoginButton
          color={ButtonColor.Primary}
          disabled={formState.isSubmitting}
          size={ButtonSize.Large}
          text={t('login_login_signIn_button')}
          type="submit"
        />
        <Copyright>
          <Trans i18nKey="general_components_copyright_text" />
        </Copyright>
        <VersionContainer>
          {t('login_login_version_label')}: {version}
        </VersionContainer>
      </WidgetContainer>
      <Information>
        <InformationTitle>
          <Trans i18nKey="login_information_title" />
        </InformationTitle>
        <InformationParagraph>
          <Trans i18nKey="login_information1_text" />
        </InformationParagraph>
        <InformationParagraph>
          <Trans i18nKey="login_information2_text" />
        </InformationParagraph>
      </Information>
    </Container>
  );
};
export default LoginWidget;
